/** Shared host checkout preview applies to any selected studio target (including registered scratch apps). */
export function usesSharedHostPreview(targetId: string | undefined): boolean {
  return targetId !== undefined && targetId.trim() !== '';
}

export const HOST_PREVIEW_STATUS_POLL_MS = 5_000;

/**
 * Host-preview status is for the shared checkout iframe on the target's host-preview path.
 * Skip while a session owns the iframe or on the apps menu.
 */
export function shouldFetchHostPreviewStatus(input: {
  readonly sessionId: string | undefined;
  readonly targetId: string | undefined;
  readonly onAppsMenu: boolean;
}): boolean {
  if (input.onAppsMenu || input.sessionId !== undefined) {
    return false;
  }
  return usesSharedHostPreview(input.targetId);
}

export function shouldReloadHostPreview(
  previousGeneration: number,
  nextGeneration: number
): boolean {
  return nextGeneration > previousGeneration;
}

/** Legacy webpack HMR updates the iframe in place; Next session preview often does not. */
export function shouldReloadPreviewAfterAgentTurn(
  wasAgentBusy: boolean,
  isAgentBusy: boolean,
  previewLive: boolean
): boolean {
  return wasAgentBusy && !isAgentBusy && previewLive;
}

/**
 * Inspect is toggled with postMessage into the live iframe.
 * Remounting would reload `previewUrl` and wipe SPA navigation back to home.
 */
export function shouldReloadPreviewOnInspectToggle(): boolean {
  return false;
}

/** Drop the ephemeral iframe remount query before reusing a captured path. */
export function normalizePreviewPath(pathWithSearchHash: string): string {
  try {
    const url = new URL(pathWithSearchHash, 'http://studio.local');
    url.searchParams.delete('_studio');
    const search = url.searchParams.toString();
    return `${url.pathname}${search === '' ? '' : `?${search}`}${url.hash}`;
  } catch {
    return pathWithSearchHash;
  }
}

/**
 * Apply a captured SPA path (pathname + search + hash) onto a preview origin URL.
 * Root paths leave the base URL unchanged.
 */
export function withPreviewPath(baseUrl: string, pathWithSearchHash: string | undefined): string {
  if (baseUrl.trim() === '' || pathWithSearchHash === undefined || pathWithSearchHash === '') {
    return baseUrl;
  }
  const normalized = normalizePreviewPath(pathWithSearchHash);
  if (normalized === '/' || normalized === '') {
    return baseUrl;
  }
  try {
    const base = new URL(baseUrl);
    const next = new URL(normalized, base.origin);
    base.pathname = next.pathname;
    base.search = next.search;
    base.hash = next.hash;
    return base.toString();
  } catch {
    return baseUrl;
  }
}

/** Remount query without breaking paths that already have search or hash. */
export function withStudioReloadQuery(previewUrl: string, reloadKey: number | undefined): string {
  if (previewUrl.trim() === '' || reloadKey === undefined || reloadKey === 0) {
    return previewUrl;
  }
  try {
    const url = new URL(previewUrl);
    url.searchParams.set('_studio', String(reloadKey));
    return url.toString();
  } catch {
    return previewUrl;
  }
}

export type ParsePreviewPathInputArgs = {
  readonly rawInput: string;
  readonly previewOriginUrl: string;
};

export type ParsePreviewPathSuccess = {
  readonly ok: true;
  readonly path: string;
};

export type ParsePreviewPathFailure = {
  readonly ok: false;
  readonly reason: 'empty-origin' | 'off-origin' | 'invalid';
};

export type ParsePreviewPathResult = ParsePreviewPathSuccess | ParsePreviewPathFailure;

/**
 * Parse a path-bar value (relative path or same-origin absolute URL).
 * Off-origin absolute URLs are rejected so the preview never leaves its origin.
 */
export function parsePreviewPathInput(args: ParsePreviewPathInputArgs): ParsePreviewPathResult {
  if (args.previewOriginUrl.trim() === '') {
    return { ok: false, reason: 'empty-origin' };
  }
  let previewOrigin: string;
  try {
    previewOrigin = new URL(args.previewOriginUrl).origin;
  } catch {
    return { ok: false, reason: 'invalid' };
  }
  const trimmed = args.rawInput.trim();
  if (trimmed === '') {
    return { ok: true, path: '/' };
  }
  if (/^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(trimmed)) {
    try {
      const absolute = new URL(trimmed);
      if (absolute.origin !== previewOrigin) {
        return { ok: false, reason: 'off-origin' };
      }
      return {
        ok: true,
        path: normalizePreviewPath(`${absolute.pathname}${absolute.search}${absolute.hash}`),
      };
    } catch {
      return { ok: false, reason: 'invalid' };
    }
  }
  const withLeadingSlash =
    trimmed.startsWith('/') || trimmed.startsWith('?') || trimmed.startsWith('#')
      ? trimmed
      : `/${trimmed}`;
  return { ok: true, path: normalizePreviewPath(withLeadingSlash) };
}

export const PREVIEW_PATH_HISTORY_CAP = 50;

export type PreviewPathHistoryState = {
  readonly entries: readonly string[];
  readonly index: number;
};

export function createPreviewPathHistory(initialPath: string = '/'): PreviewPathHistoryState {
  const normalized = normalizePreviewPath(initialPath);
  const path = normalized === '' ? '/' : normalized;
  return { entries: [path], index: 0 };
}

export type PushPreviewPathHistoryArgs = {
  readonly history: PreviewPathHistoryState;
  readonly path: string;
  readonly cap?: number;
};

export function pushPreviewPathHistory(args: PushPreviewPathHistoryArgs): PreviewPathHistoryState {
  const normalized = normalizePreviewPath(args.path);
  const path = normalized === '' ? '/' : normalized;
  const current = args.history.entries[args.history.index];
  if (current === path) {
    return args.history;
  }
  const cap = args.cap ?? PREVIEW_PATH_HISTORY_CAP;
  const truncated = args.history.entries.slice(0, args.history.index + 1);
  const nextEntries = [...truncated, path];
  const capped =
    nextEntries.length > cap ? nextEntries.slice(nextEntries.length - cap) : nextEntries;
  return { entries: capped, index: capped.length - 1 };
}

export function previewPathHistoryCanBack(history: PreviewPathHistoryState): boolean {
  return history.index > 0;
}

export function previewPathHistoryCanForward(history: PreviewPathHistoryState): boolean {
  return history.index < history.entries.length - 1;
}

export function previewPathHistoryBack(
  history: PreviewPathHistoryState
): PreviewPathHistoryState | undefined {
  if (!previewPathHistoryCanBack(history)) {
    return undefined;
  }
  return { entries: history.entries, index: history.index - 1 };
}

export function previewPathHistoryForward(
  history: PreviewPathHistoryState
): PreviewPathHistoryState | undefined {
  if (!previewPathHistoryCanForward(history)) {
    return undefined;
  }
  return { entries: history.entries, index: history.index + 1 };
}

export function previewPathHistoryCurrent(history: PreviewPathHistoryState): string {
  return history.entries[history.index] ?? '/';
}

export type PreviewPathPresetId = 'home' | 'catalogue' | 'diaporama';

export type PreviewPathPreset = {
  readonly id: PreviewPathPresetId;
  readonly path: string;
};

/** Marketplace-oriented presets; other targets only get Accueil. */
export function previewPathPresetsForTarget(
  targetId: string | undefined
): readonly PreviewPathPreset[] {
  const home: PreviewPathPreset = { id: 'home', path: '/' };
  if (targetId === 'marketplace' || targetId === 'marketplace-site') {
    return [
      home,
      { id: 'catalogue', path: '/nos-vehicules' },
      { id: 'diaporama', path: '/nos-vehicules/diaporama' },
    ];
  }
  return [home];
}
